import type { Website } from 'types';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useWebsites } from '@/state/useWebsites';
import { SearchInput } from '@/components/SearchInput';
import { Plus, Trash2, BadgeCheck, BadgeAlert, Globe } from 'lucide-react';
import { DataTable, useDataControl } from '@/components/DataTable';
import { FirstRunState } from '../../FirstRunState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { useForm } from 'hooks/useForm';
import { useAuth } from 'hooks/useAuth';
import { can } from 'definitions/capabilities';
import { canUseServerSide } from 'definitions/server-side-access';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { cn } from 'helpers/cn';
import { FOCUS_RING, MICRO_LABEL_SHAPE, SEARCH_FIELD_CLASS } from '../../ConsolePanel';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ScriptsModal } from './ScriptsModal';
import { ServerContainerUrl } from './ServerContainerUrl';
import { WebsiteSettingsModal } from './WebsiteSettingsModal';
import { EventGoalsModal } from './EventGoalsModal';
import { ServerKeysModal } from './ServerKeysModal';
import { TRACKING_LABEL } from '../tracking-methods';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';

export function WebsitesList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Server keys are server-side, staff-only until it launches (PER-178); the API refuses the rest.
  const canManageServerKeys = can(user, 'server-keys:manage') && canUseServerSide(user);
  const manager = useWebsites();
  const { websites, setWebsite, createWebsite, deleteWebsite } = manager;
  const { recheckPermissions, rechecking, accountOptedOut } = manager;
  const [isCreating, setIsCreating] = useState(false);
  const { formRef, state: newWebsite, clear } = useForm<NewWebsiteType>({});
  const { url } = newWebsite;
  const dataController = useDataControl<Website>(websites || []);
  const { searchQuery, setSearchQuery } = dataController;

  useEffect(() => {
    const { state, pathname } = location;
    if (state?.create) {
      setIsCreating(true);
      navigate(pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  if (!websites) return null;

  /**
   * The dialog stays open when this fails, so the bad URL is still there to correct.
   * Closing it and toasting would make the user retype what they got wrong.
   */
  const handleCreateWebsite = async () => {
    try {
      await createWebsite(newWebsite);
      setIsCreating(false);
      clear();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create the website.');
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    clear();
  };

  const handleDelete = async (id: number) => {
    await deleteWebsite(id);
  };

  return (
    <div className="flex flex-col gap-[14px]">
      <ConfirmDialog
        open={isCreating}
        title="Create New Website"
        disabled={!url}
        onConfirm={handleCreateWebsite}
        onCancel={handleCancelCreate}
      >
        <form ref={formRef} className="grid gap-5">
          <div className="grid gap-2">
            <label htmlFor="new-website-url" className="micro-label block">
              Website URL
            </label>
            <Input
              id="new-website-url"
              name="url"
              type="url"
              placeholder="https://example.com"
              className={FIELD_INPUT}
            />
          </div>
        </form>
      </ConfirmDialog>

      <section className="bg-panel border-line rounded-panel border">
        {/* Stacked below `md`: the title, the search and the button do not share one
            375px line. `relative` on the search wrapper because SearchInput collapses
            to a magnifier and drops its field in absolutely. */}
        <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
          <h2 className="text-foreground m-0 text-body font-medium">Websites</h2>
          <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">
            <div className="relative w-full md:w-auto">
              <SearchInput
                placeholder="Search websites..."
                value={searchQuery}
                handleSearch={setSearchQuery}
                className={SEARCH_FIELD_CLASS}
              />
            </div>
            <Button
              onClick={() => setIsCreating(true)}
              className="h-9 w-full rounded-lg md:w-auto"
              data-tour="websites-create"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Website
            </Button>
          </div>
        </div>

        <div className="p-3">
          {dataController.rawData?.length === 0 ? (
            <FirstRunState
              icon={Globe}
              title="No websites yet"
              description="A website is a site you want to test. Add its address, then put one line of script in its pages and Pertento can run experiments and surveys there."
              action={
                <Button onClick={() => setIsCreating(true)} className="h-9 rounded-lg">
                  <Plus className="mr-2 h-4 w-4" />
                  Add your first website
                </Button>
              }
            />
          ) : (
            <DataTable
              dataController={dataController}
              searchKeys={['url']}
              pageSize={10}
              noDataMessage="No websites found."
              noDataCTA={
                <Button onClick={() => setIsCreating(true)} className="mt-4 h-9 w-full rounded-lg min-[500px]:w-auto">
                  <Plus className="mr-2 h-4 w-4" />
                  Create your first Website
                </Button>
              }
              columns={[
                {
                  field: 'id',
                  label: 'ID',
                  headerClassName: 'micro-label w-20',
                  cellClassName: 'num',
                  sortKey: 'id',
                  mobile: 'meta',
                  format: ({ value, idx }) => (
                    <button
                      onClick={() => setWebsite(value as number)}
                      className="text-accent num hover:underline"
                      data-tour={idx === 0 ? 'websites-select' : undefined}
                    >
                      {value as number}
                    </button>
                  ),
                },
                {
                  field: 'url',
                  label: 'URL',
                  sortKey: 'url',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  // The site is what a row IS, so it heads the card on a phone.
                  mobile: 'title',
                  format: ({ value, item }) => {
                    const urlStr = value as string;
                    const showFavicon = urlStr?.startsWith('https');
                    return (
                      <button
                        type="button"
                        onClick={() => navigate(`/websites/${item.id}`)}
                        title={urlStr}
                        className={cn(
                          'text-foreground flex min-h-7 min-w-0 items-center gap-2 rounded-sm hover:underline',
                          FOCUS_RING,
                        )}
                      >
                        {/* Light plate in both themes: a favicon is drawn for its
                          owner's background, almost always a light one, so a
                          black-on-transparent mark disappears on a dark row. */}
                        {showFavicon && (
                          <span className="border-line bg-plate flex size-5 shrink-0 items-center justify-center rounded border p-0.5">
                            <Avatar className="size-full rounded-none">
                              <AvatarImage src={`${urlStr}/favicon.ico`} className="object-contain" />
                            </Avatar>
                          </span>
                        )}
                        <span className="max-w-50 truncate">{urlStr}</span>
                      </button>
                    );
                  },
                },
                {
                  field: 'id',
                  label: 'Scripts',
                  headerClassName: 'micro-label w-32',
                  mobile: 'action',
                  // Anchors go on the first row only, so each tour step finds one element.
                  format: ({ value, idx }) => (
                    <ScriptsModal websiteId={value as number} tourAnchor={idx === 0 ? 'websites-scripts' : undefined} />
                  ),
                },
                {
                  field: 'serverContainerUrl',
                  label: 'Server Container URL',
                  labelMobile: 'Container',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  mobile: 'meta',
                  // The server-side container is Google/GTM plumbing; Pertento tracking
                  // reports to the Pertento tracking server and never uses it, so the
                  // editor would only invite configuring a URL nothing reads.
                  format: ({ item, idx }) =>
                    item.trackingMethod === 'pertento' ? (
                      <span
                        className="text-dim num"
                        title="Not used with Pertento tracking"
                        data-tour={idx === 0 ? 'websites-container' : undefined}
                      >
                        -
                      </span>
                    ) : (
                      <ServerContainerUrl website={item} tourAnchor={idx === 0 ? 'websites-container' : undefined} />
                    ),
                },
                {
                  field: 'trackingMethod',
                  label: 'Tracking',
                  sortKey: 'trackingMethod',
                  headerClassName: 'micro-label w-56',
                  // The row's only health signal, so it stays on the card.
                  mobile: 'meta',
                  /*
                   * One column for "how is this site measured", instead of three Google
                   * ones (Property ID, Measurement ID, Can Edit?) that rendered as dashes
                   * on every non-Google row. A Pertento- or Matomo-tracked site says so in
                   * one chip; only a row with a linked Google property pays for the
                   * property id and the write-access check. The measurement id moved to
                   * the settings modal — a diagnostic identifier looked up once, not worth
                   * a column on every render.
                   */
                  format: ({ item, idx }) => {
                    const canEdit = (item.ganProperty?.hasEditPermission?.length ?? 0) > 0;

                    return (
                      <div
                        className="flex flex-wrap items-center gap-2"
                        data-tour={idx === 0 ? 'websites-tracking' : undefined}
                      >
                        {item.trackingMethod ? (
                          <span className="border-line text-dim rounded-pill border px-2 py-0.5 font-mono text-nano uppercase leading-[1.6] tracking-[0.08em]">
                            {TRACKING_LABEL[item.trackingMethod]}
                          </span>
                        ) : (
                          <span className="text-dim num" title="No tracking method configured">
                            -
                          </span>
                        )}
                        {item.ganProperty && (
                          <>
                            <span className="num text-dim">{item.ganPropertyId}</span>
                            {/*
                            The word, not just the badge: a teal check against a red alert
                            is unreadable to a red-green reader and silent to a screen
                            reader. Write access either works (ok) or is missing (danger);
                            no stale case, the check is re-run on demand, not cached.
                          */}
                            <span className={cn('flex items-center gap-1.5', canEdit ? 'text-ok' : 'text-danger')}>
                              {canEdit ? (
                                <BadgeCheck className="size-4 shrink-0" aria-hidden="true" />
                              ) : (
                                <BadgeAlert className="size-4 shrink-0" aria-hidden="true" />
                              )}
                              <span className={MICRO_LABEL_SHAPE}>{canEdit ? 'Can edit' : 'No access'}</span>
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={rechecking}
                              title={
                                rechecking
                                  ? 'Checking write access with Google…'
                                  : 'Re-check write access to this Google Analytics property'
                              }
                              onClick={() => recheckPermissions(item.ganPropertyId!)}
                              className="micro-label text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg px-2"
                            >
                              {rechecking ? 'Rechecking...' : 'Recheck'}
                            </Button>
                          </>
                        )}
                      </div>
                    );
                  },
                },
                {
                  field: 'id',
                  label: 'Event Goals',
                  headerClassName: 'micro-label w-40',
                  mobile: 'action',
                  /*
                   * Not gated on ganPropertyId any more. The registry this opens is filled by
                   * the tracking server from whatever the site reports, so a Pertento-tracked
                   * website with no Google Analytics property has event goals to map — and this
                   * modal is the only route to them, the website details screen having no route
                   * in menu-links.tsx. Gating it here made the mapping unreachable for exactly
                   * the websites it is meant for.
                   */
                  format: ({ item, idx }) => (
                    <EventGoalsModal website={item} tourAnchor={idx === 0 ? 'websites-goals' : undefined} />
                  ),
                },
                {
                  field: 'id',
                  label: 'Actions',
                  headerClassName: 'micro-label w-32',
                  mobile: 'action',
                  format: ({ item, idx }) => (
                    <div className="flex items-center gap-1">
                      {canManageServerKeys && (
                        <ServerKeysModal website={item} tourAnchor={idx === 0 ? 'websites-server-keys' : undefined} />
                      )}
                      {/* Data feeds live in the settings dialog: the wrapper carries their own tour step. */}
                      <span className="inline-flex" data-tour={idx === 0 ? 'websites-feeds' : undefined}>
                        <WebsiteSettingsModal
                          website={item}
                          accountOptedOut={accountOptedOut}
                          tourAnchor={idx === 0 ? 'websites-settings' : undefined}
                        />
                      </span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete website ${item.url}`}
                            title={`Delete website ${item.url}`}
                            className="text-dim hover:bg-danger-bg hover:text-danger h-8 w-8 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Website</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this website? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-line rounded-lg bg-transparent">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(item.id)}
                              className="bg-danger-bg border-danger-line text-danger hover:bg-danger-bg rounded-lg border"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      </section>
    </div>
  );
}

type NewWebsiteType = {
  url: string;
};
